/**
 * Antenna proportions shared by the generator (three.js) and the spec builder
 * (no three.js, lives in the app shell chunk). Metres.
 */
import type { PedestalType, ProceduralAntennaSpec } from '../types';

/** Height of the elevation axis above ground for a dish of diameter d on a pedestal type. */
export function elevationAxisHeight(d: number, pedestal: PedestalType): number {
  switch (pedestal) {
    case 'wheel-and-track':
      return 0.6 * d;
    case 'king-post':
      return Math.max(1.8, 0.58 * d);
    case 'turret':
      return Math.max(1.4, 0.55 * d);
    case 'tripod':
      return Math.max(1.2, 0.55 * d);
    case 'tower':
      return Math.max(3.5, 1.6 * d);
  }
}

/** Approximate bounding sphere of the generated antenna site (dish, mast and optional container). */
export function estimateAntennaBounds(spec: ProceduralAntennaSpec): { centre: [number, number, number]; radius: number } {
  const d = spec.diameterM;
  if (spec.rig === 'optical') return { centre: [0, 2.2, 0], radius: 4.5 };
  const yAxle = elevationAxisHeight(d, spec.pedestal);
  const top = yAxle + 0.65 * d;
  const xMin = -(0.45 * d + 3);
  const xMax = spec.container ? 0.55 * d + 3.5 + 3 : 0.6 * d;
  const zHalf = Math.max(0.6 * d, spec.container ? 0.2 * d + 3.5 : 0);
  const cx = (xMin + xMax) / 2;
  const cy = top / 2;
  const radius = Math.hypot((xMax - xMin) / 2, top / 2, zHalf) * 0.85;
  return { centre: [cx, cy, 0], radius };
}
